/**
 * Fase 2 Sub-release 2.6 — Catalogo Offerte Cliente
 *
 * Landing page del cliente (post-login se ha 0 preventivi salvati). Mostra le
 * auto filtrate per le sue preferenze (durata, km, fascia budget) con card che
 * permettono di:
 *  - Vedere canone base per la durata/km preferita
 *  - Salvare fino a MAX 3 preventivi (Visto Verde workflow ereditato)
 *  - Aprire dettaglio veicolo (link a /dashboard/offerte dopo save)
 *
 * Dipende dai preferenze salvate (`/api/client/preferenze-quote`). Se assenti,
 * apre il modal PreferenzeQuoteDialog al mount.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Loader2, Car, Sparkles, Handshake, CheckCircle2, Settings, Bookmark, BookmarkCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import PreferenzeQuoteDialog from '../components/PreferenzeQuoteDialog';
import { useAuth } from '../context/AuthContext';
import { useVehicleImage } from '../hooks/useVehicleImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 2.6.10 — Thumbnail veicolo per card catalogo cliente.
 * Prima usa link_immagine se presente, altrimenti fa fetch da Google Drive.
 */
const CardVehicleImage = ({ marca, modello, linkImmagine }) => {
  const skipDrive = Boolean(linkImmagine);
  const { imageUrl: driveUrl } = useVehicleImage(skipDrive ? null : marca, skipDrive ? null : modello);
  const finalUrl = linkImmagine || driveUrl;
  return finalUrl ? (
    <img
      src={finalUrl}
      alt={`${marca} ${modello}`}
      className="w-full h-full object-contain"
      loading="lazy"
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  ) : (
    <div className="w-full h-full flex items-center justify-center text-slate-300">
      <Car className="w-12 h-12" />
    </div>
  );
};

const FASCIA_LABELS = {
  lt_200: 'Fino a 200€',
  '201_400': '201€ - 400€',
  '401_600': '401€ - 600€',
  gt_600: 'Oltre 600€',
};

function fmtEuro(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `€${Math.round(v).toLocaleString('it-IT')}`;
}

const CatalogoCard = ({ v, is4v = false, saving, slotFull, slotMax, onSave, navigate }) => {
  const tid = is4v ? 'catalogo-4v' : 'catalogo';
  return (
      <Card
            className={`overflow-hidden transition hover:shadow-md ${v.gia_salvato ? 'ring-2 ring-emerald-400' : ''} ${is4v ? 'border-[#7B1FA2]/40' : ''}`}
        data-testid={`${tid}-card-${v.id}`}
      >
        <div className="h-40 bg-slate-100 relative overflow-hidden">
          <CardVehicleImage marca={v.marca} modello={v.modello} linkImmagine={v.link_immagine} />
          <Badge className={`absolute top-2 left-2 text-[10px] uppercase ${v.tipo === 'nuovo' ? 'bg-emerald-600' : 'bg-amber-500'} text-white`}>
            {v.tipo}
          </Badge>
          {is4v && (
            <Badge className="absolute bottom-2 left-2 text-[10px] uppercase text-white" style={{ backgroundColor: '#7B1FA2' }} data-testid={`catalogo-4v-badge-${v.id}`}>
              4Vantage
            </Badge>
          )}
          {v.gia_salvato && (
            <Badge className="absolute top-2 right-2 bg-white text-emerald-700 border border-emerald-300 flex items-center gap-1">
              <BookmarkCheck className="w-3 h-3" /> Salvata
            </Badge>
          )}
        </div>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs uppercase text-slate-500 tracking-wide">{v.marca}</p>
            {v.vehicle_id && (
              <span className="text-[10px] font-mono text-slate-400" data-testid={`vehicle-external-id-${v.id}`}>
                ID {v.vehicle_id}
              </span>
            )}
          </div>
          <p className="text-base font-bold text-slate-800 line-clamp-1">{v.modello}</p>
          {v.versione && <p className="text-xs text-slate-500 line-clamp-1">{v.versione}</p>}
          <div className="flex items-center gap-2 flex-wrap pt-1">
            {v.alimentazione && <span className="text-[11px] text-slate-500">{v.alimentazione}</span>}
            {v.cambio && <span className="text-[11px] text-slate-500">· {v.cambio}</span>}
          </div>
          <div className="pt-2 border-t border-slate-100">
            {is4v && v.canone_massimo > (v.canone_finale ?? 0) && (
              <p className="text-xs text-slate-400" data-testid={`${tid}-canone-standard-${v.id}`}>
                <span className="text-[10px] uppercase">Canone standard </span>
                <span className="line-through decoration-slate-400">{fmtEuro(v.canone_massimo)}/mese</span>
              </p>
            )}
            <p className="text-[10px] uppercase text-slate-400">Canone convenzione</p>
            <p className={`text-xl font-bold ${is4v ? 'text-[#7B1FA2]' : 'text-emerald-700'} flex items-baseline gap-1`} data-testid={`${tid}-canone-${v.id}`}>
              {fmtEuro(v.canone_finale ?? v.canone_base)}
              <span className="text-xs font-normal text-slate-500">/mese</span>
              {v.canone_fallback && (
                <span
                  title="Quotazione stimata sulla combinazione più vicina"
                  className="text-[10px] text-amber-600 ml-1"
                >
                  ~
                </span>
              )}
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {is4v
                ? <span className="text-[#7B1FA2] font-medium" data-testid={`catalogo-4v-parametri-${v.id}`}>48 Mesi · 30.000 km/anno · Gomme Incluse · Franchigia Kasko €500 · Furto 0%</span>
                : `${v.durata_mesi} mesi · ${(v.km_annui || 0).toLocaleString('it-IT')} km/anno`}
            </p>
          </div>
          <div className="pt-2">
            {v.gia_salvato ? (
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-1.5"
                onClick={() => navigate('/dashboard/offerte')}
                data-testid={`${tid}-btn-vai-preventivi-${v.id}`}
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Vai al preventivo
              </Button>
            ) : (
              <Button
                size="sm"
                className="w-full gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => onSave(v)}
                disabled={saving === v.id || (slotFull && !v.gia_salvato)}
                data-testid={`${tid}-btn-salva-preventivo-${v.id}`}
              >
                {saving === v.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : slotFull ? (
                  <>
                    <Handshake className="w-4 h-4" /> Slot pieni ({slotMax}/{slotMax})
                  </>
                ) : (
                  <>
                    <Bookmark className="w-4 h-4" /> Salva tra i preventivi
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
  );
};

export default function ClienteCatalogoPage() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [items4v, setItems4v] = useState([]);
  const [sortMode, setSortMode] = useState('canone');
  const [preferenze, setPreferenze] = useState(null);
  const [slotUsed, setSlotUsed] = useState(0);
  const [slotMax, setSlotMax] = useState(3);
  const [totalMatching, setTotalMatching] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);  // vehicle_id in corso di save
  const [showPrefs, setShowPrefs] = useState(false);

  const reqId = useRef(0);
  const load = useCallback(async () => {
    const myReq = ++reqId.current;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/client/catalogo?limit=60`, { headers: getAuthHeader() });
      if (myReq !== reqId.current) return;
      setItems(res.data?.items || []);
      setPreferenze(res.data?.preferenze || null);
      setSlotUsed(res.data?.slot_used || 0);
      setSlotMax(res.data?.slot_max || 3);
      setTotalMatching(res.data?.total_matching || 0);
      // 4Vantage solo per dipendenti / P.IVA forfettarie (in fondo al catalogo)
      if (res.data?.preferenze?.abilita_4vantage) {
        const res4v = await axios.get(`${API}/client/catalogo?limit=60&tipo=4vantage`, { headers: getAuthHeader() })
          .catch(() => ({ data: { items: [] } }));
        if (myReq !== reqId.current) return;
        setItems4v(res4v.data?.items || []);
      } else {
        setItems4v([]);
      }
      // 2.6.14b — Se manca durata/km O la risposta alla domanda 4Vantage (bool),
      // riapri il dialog per completare il funnel. Compatibile con clienti che avevano
      // il legacy `profilo_fiscale`: se profilo_fiscale="piva_forfettaria" → considera Sì.
      const p = res.data?.preferenze;
      const has4vFlag = typeof p?.abilita_4vantage === 'boolean'
        || p?.profilo_fiscale === 'piva_forfettaria'
        || p?.profilo_fiscale === 'dipendente';
      const hasPrefs = p?.durata && p?.km && has4vFlag;
      if (!hasPrefs && myReq === reqId.current) setShowPrefs(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel caricamento del catalogo');
    } finally {
      if (myReq === reqId.current) setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const handleSalvaPreventivo = async (item) => {
    if (slotUsed >= slotMax) {
      toast.error(`Hai già ${slotMax} preventivi salvati. Rimuovine uno da "I Miei Preventivi" per aggiungerne un altro.`);
      return;
    }
    setSaving(item.id);
    try {
      await axios.post(
        `${API}/client/preventivi/save`,
        { vehicle_id: item.id, durata: item.durata_mesi, km: item.km_annui, listino: item.listino },
        { headers: getAuthHeader() },
      );
      toast.success(`${item.marca} ${item.modello} aggiunto ai tuoi preventivi.`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio del preventivo');
    } finally {
      setSaving(null);
    }
  };

  const slotFull = slotUsed >= slotMax;
  const cardProps = { saving, slotFull, slotMax, onSave: handleSalvaPreventivo, navigate };
  const mixed = [
    ...items.map((v) => ({ v, is4v: false })),
    ...items4v.map((v) => ({ v, is4v: true })),
  ].sort((a, b) => {
    if (sortMode === 'alfabetico') {
      const ka = `${a.v.marca || ''} ${a.v.modello || ''} ${a.v.versione || ''} ${a.v.id}`;
      const kb = `${b.v.marca || ''} ${b.v.modello || ''} ${b.v.versione || ''} ${b.v.id}`;
      return ka.localeCompare(kb, 'it') || (a.is4v - b.is4v) || ((a.v.canone_finale || 0) - (b.v.canone_finale || 0));
    }
    return ((a.v.canone_finale || 0) - (b.v.canone_finale || 0)) || (a.is4v - b.is4v);
  });

  return (
    <div className="space-y-6" data-testid="cliente-catalogo-page">
      {/* Header + preferenze */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Catalogo Offerte
          </h1>
          <p className="text-slate-500 mt-1">
            Auto selezionate per te in base alle tue preferenze. Salva fino a {slotMax} preventivi.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowPrefs(true)}
          className="gap-1.5"
          data-testid="btn-edit-preferenze"
        >
          <Settings className="w-4 h-4" /> Modifica preferenze
        </Button>
      </div>

      {/* Preferenze attive + slot */}
      <div className="flex flex-wrap gap-2 items-center">
        {preferenze?.durata && (
          <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">
            Durata: {preferenze.durata} mesi
          </Badge>
        )}
        {preferenze?.km && (
          <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">
            {preferenze.km.toLocaleString('it-IT')} km/anno
          </Badge>
        )}
        {preferenze?.fascia_budget && (
          <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">
            Budget: {FASCIA_LABELS[preferenze.fascia_budget] || preferenze.fascia_budget}
          </Badge>
        )}
        <span className="ml-auto text-xs text-slate-500">
          <strong>{slotUsed}/{slotMax}</strong> slot preventivi usati
          {slotFull && (
            <Button
              variant="link"
              size="sm"
              className="px-1 h-auto text-xs text-emerald-700"
              onClick={() => navigate('/dashboard/offerte')}
              data-testid="link-gestisci-preventivi"
            >
              Gestisci →
            </Button>
          )}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 && items4v.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center space-y-3">
            <Sparkles className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="text-slate-500">
              Nessuna auto trovata con le preferenze attuali.
            </p>
            <Button variant="outline" onClick={() => setShowPrefs(true)}>
              Modifica le preferenze
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {items4v.length > 0 && (
            <div
              className="rounded-xl px-5 py-4 text-white flex items-start gap-3 shadow-sm"
              style={{ backgroundColor: '#7B1FA2' }}
              data-testid="catalogo-4vantage-banner"
            >
              <Sparkles className="w-5 h-5 shrink-0 mt-0.5" />
              <p className="text-sm sm:text-base">
                Le offerte contraddistinte dal badge viola <strong>4Vantage</strong> sono riservate esclusivamente a Dipendenti e P.IVA Forfettarie. Prevedono condizioni fisse: durata 48 mesi, 30.000 km/anno inclusi, treno di gomme compreso, franchigia Kasko € 500 e Furto/Incendio 0%.
              </p>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-500" data-testid="catalogo-count">
              <strong>{totalMatching + items4v.length}</strong> offerte in linea con le tue preferenze
              {items4v.length > 0 && ` (di cui ${items4v.length} 4Vantage)`}
              {totalMatching > items.length && ` — mostrate le prime ${items.length} standard`}
            </p>
            <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 text-xs" data-testid="catalogo-sort">
              {[['canone', 'Canone crescente'], ['alfabetico', 'Marca e modello']].map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setSortMode(k)}
                  className={`px-3 py-1.5 rounded-full transition-colors ${sortMode === k ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  data-testid={`catalogo-sort-${k}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="catalogo-grid">
            {mixed.map(({ v, is4v }) => (
              <CatalogoCard key={`${is4v ? '4v' : 'std'}-${v.id}`} v={v} is4v={is4v} {...cardProps} />
            ))}
          </div>
        </>
      )}

      {/* Modal preferenze */}
      <PreferenzeQuoteDialog
        open={showPrefs}
        onClose={() => setShowPrefs(false)}
        onSaved={() => { reqId.current += 1; setShowPrefs(false); load(); }}
        initialDurata={preferenze?.durata || 48}
        initialKm={preferenze?.km || 15000}
        initialFasciaBudget={preferenze?.fascia_budget || null}
        initialAbilita4Vantage={
          typeof preferenze?.abilita_4vantage === 'boolean'
            ? preferenze.abilita_4vantage
            : (preferenze?.profilo_fiscale === 'piva_forfettaria'
                ? true
                : (preferenze?.profilo_fiscale === 'dipendente' ? true : null))
        }
      />
    </div>
  );
}
